import axios from "axios";
import { useQuery } from "@tanstack/react-query";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 20000 });

const fetcher = (path) => async () => (await api.get(path)).data;

export const useConfig = () =>
  useQuery({ queryKey: ["config"], queryFn: fetcher("/config"), staleTime: Infinity });

export const useServices = () =>
  useQuery({ queryKey: ["services"], queryFn: fetcher("/services"), staleTime: Infinity });

export const useProcess = () =>
  useQuery({ queryKey: ["process"], queryFn: fetcher("/process"), staleTime: Infinity });

export const useProjects = () =>
  useQuery({ queryKey: ["projects"], queryFn: fetcher("/projects"), staleTime: Infinity });

export const useProject = (slug) =>
  useQuery({ queryKey: ["project", slug], queryFn: fetcher(`/projects/${slug}`), enabled: !!slug, retry: 0 });

export const useStats = () =>
  useQuery({ queryKey: ["stats"], queryFn: fetcher("/stats"), staleTime: Infinity });

export const useProjectTypes = () =>
  useQuery({ queryKey: ["project-types"], queryFn: fetcher("/project-types"), staleTime: Infinity });

// ---- image helpers (Unsplash width params) ----
export const imgAt = (url, w) => {
  if (!url) return url;
  return /([?&])w=\d+/.test(url) ? url.replace(/([?&])w=\d+/, `$1w=${w}`) : `${url}${url.includes("?") ? "&" : "?"}w=${w}`;
};

export const imgSrcSet = (url, widths = [640, 1080, 1600, 2200]) =>
  widths.map((w) => `${imgAt(url, w)} ${w}w`).join(", ");

// ---- Web3Forms (client-side delivery; free plan only accepts browser submissions) ----
export const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY || "";
export const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";

/** Posts a prepared payload (from the backend) to Web3Forms. Returns true on success. */
export const sendWeb3Forms = async (payload) => {
  if (!WEB3FORMS_KEY || !payload) return false;
  try {
    const res = await fetch(WEB3FORMS_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ access_key: WEB3FORMS_KEY, ...payload }),
    });
    const data = await res.json().catch(() => ({}));
    return res.ok && data?.success === true;
  } catch (e) {
    return false;
  }
};

// ---- config helpers ----
export const formatAddress = (cfg) =>
  cfg ? `${cfg.address.street}, ${cfg.address.zip} ${cfg.address.city}` : "";

export const mapsUrl = (cfg) =>
  cfg
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${cfg.legal_name}, ${cfg.address.street}, ${cfg.address.zip} ${cfg.address.city}`,
      )}`
    : "#";
